<script setup>
import Navbar from './Navbar.vue';
import UserInfo from './UserInfo.vue';
import TourContainer from '/app/components/home/TourContainer.vue';
import CreateChallengeIcon from '/app/components/home/CreateChallengeIcon.vue';
import Markdown from '/app/components/NMarkdown.vue';
import NIcon from '/app/components/NIcon.vue';
import NTip from '/app/components/NTip.vue';
import { Session } from '/app/utils/auth';
import { useFeatureFlag } from '/app/composables/feature-flag';
import { watch, ref, computed } from 'vue';
import { usePlatform, useUserPlatform, usePlatformUtils } from '/app/composables/platform';
import { useBus } from '/app/composables/bus';

import { useRoute } from 'vue-router';
import { useReaderMode } from '/app/composables/app';
import { crossPlatformState } from '/app/utils/app';
import { useModerationContext } from '/app/composables/moderation';
import { useAuth } from '/app/composables/auth';

const { on, emit: $bus } = useBus()
const { auth: $auth } = useAuth();
const { abilities } = useModerationContext();

const readingState = crossPlatformState('bignerve.reading');
const route = useRoute()
const { account, isSuspended } = useUserPlatform()
const { contextPlatform } = usePlatform()
const { isUserPlatformView, isUserPlatform, isSubplatform } = usePlatformUtils()
const { enabled: readerMode } = useReaderMode()

const bgImageLoaded = ref(false)

const computedStyle = computed(() => {
    if (!contextPlatform.value?.masthead_background_image) {
        return {}
    }

    return {
        backgroundImage: `url(${contextPlatform.value?.masthead_background_image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'top',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backgroundRepeat: 'no-repeat',
    }
})

watch(() => contextPlatform.value?.masthead_background_image, (url) => {
    bgImageLoaded.value = false
    if (!url) return

    const img = new Image()
    img.onload = () => { bgImageLoaded.value = true }
    img.src = url
}, { immediate: true })

const headerRef = ref(null)
const hideHeader = computed(() => !route.name || route.meta.hideHeader)
const isSplashPage = computed(() => route.meta.splash)
const showNavbar = computed(() => route.meta.navbar ?? !isSplashPage.value)
const showHero = computed(() => route.meta.showHero ?? true)

const homePageRoute = computed(() => {
    if (!isSubplatform.value && isUserPlatform.value && account.value?.user?.id) {
        return {
            name: 'home.user',
            params: {
                platformSlug: account.value.user.screen_name
            }
        }
    }

    return { name: 'home.challenges' }
})

const scrollToTop = () => {
    const header = headerRef.value

    if (header) {
        header.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

const { enabled: readerModeEnabled } = useFeatureFlag('challenge-reader-mode-toggle');

const renderReaderMode = (active) => {
    if (!active || !readerModeEnabled.value) {
        readingState["reader-mode"] = null

        document.documentElement.classList.remove("reader")

        return
    }

    document.documentElement.classList.add("reader")
}

watch(readerMode, (active) => {
    renderReaderMode(active)
}, { immediate: true });

// ── Platform Welcome Message ────────────────────────────────────
const { variantTo: welcomeVariantTo } = useFeatureFlag('platform-welcome-message');
const welcomeVariant = welcomeVariantTo(() => contextPlatform.value?.slug);

on('header:scroll-to-top', scrollToTop)
on('app:reader-mode:toggle', renderReaderMode)
</script>

<template>
    <header ref="headerRef" class="app-header">
        <Navbar v-if="showNavbar && !hideHeader" />
        <div id="navbar-notifications" :class="{ 'mt-[2.7rem]': (!isSplashPage || showNavbar) && !hideHeader }"></div>
        <div class="relative">
            <div :style="computedStyle"
                class="absolute top-0 left-0 w-full h-full z-[-1] transition-opacity duration-700"
                :class="[bgImageLoaded ? 'opacity-100' : 'opacity-0', { grayscale: isUserPlatformView && isSuspended }]"></div>

            <TourContainer />

            <div v-if="showHero && !hideHeader" class="container mx-auto app-headbar">
               
                <div class="grid grid-cols-12 gap-4 py-4">
                    <div class="app-headbar__col is-first col-span-12"
                        :class="{ 'sm:col-span-4': !isUserPlatformView, 'sm:col-span-6': isUserPlatformView }">
                        <RouterLink :to="homePageRoute" title="View this User's Platform Home"
                            class="app-headbar-heading flex items-center space-x-3"
                            :class="{ 'grayscale opacity-60': isUserPlatformView && isSuspended }">
                            <img src="/img/brain.svg" alt="A Nerve Platform Logo" class="w-16" aria-hidden="true">
                            <div>
                                <span class="app-headbar-heading__title"
                                    :class="{ '!text-white text-border': !!contextPlatform?.masthead_background_image }">
                                    {{ contextPlatform?.title || 'Big' }}
                                    <b v-if="isUserPlatformView" style="font-weight: 300; color: #bbb;">Nerve</b>
                                </span>
                                <span v-if="contextPlatform?.slogan" class="app-headbar-heading__subtitle"
                                    :class="{ '!text-white text-border': !!contextPlatform?.masthead_background_image }">{{
                                        contextPlatform?.slogan }}</span>
                            </div>
                        </RouterLink>
                    </div>
                    <div v-if="!isSplashPage && !isUserPlatformView" class="col-span-12 sm:col-span-5 app-headbar__col is-middle">

                        <NTip :open="!Session.exists() && !!welcomeVariant"
                            :title="`Welcome to ${contextPlatform?.title ?? 'BigNerve'}!`"
                            :target="`welcome_message_seen:${contextPlatform?.slug ?? ''}`" class="" placement="bottom">
                            <template #description>
                                <Markdown v-if="welcomeVariant?.value" :code="welcomeVariant.payload"
                                    class="prose-p:my-1 text-white prose-a:text-white" />

                                <div v-else>
                                    {{ contextPlatform?.slogan }}
                                </div>
                            </template>

                            <p v-if="contextPlatform?.marketing_phrase" class="app-headbar__slogen"
                                :class="{ '!text-white text-border': !!contextPlatform?.masthead_background_image }">
                                {{ contextPlatform?.marketing_phrase }}
                            </p>

                        </NTip>
                    </div>

                    <div v-if="!isSplashPage && !(isUserPlatformView && isSuspended)" class="app-headbar__col is-last col-span-12"
                        :class="{ 'sm:col-span-3': !isUserPlatformView, 'sm:col-span-6': isUserPlatformView }">
                        <Transition name="entered" mode="out-in">
                            <UserInfo v-if="isUserPlatformView && account?.user?.id" :key="account?.user?.id" />

                            <div v-else-if="$auth.authenticated" key="helper-panel"
                                :disabled="!abilities?.can('create', 'game.challenge')"
                                :class="{ disabled: !abilities?.can('create', 'game.challenge') }"
                                class="flex justify-end w-full app-headbar__btn-helper-wrapper">
                                <a href="/challenges/create"
                                    class="btn justify-center items-center gap-2 is-red !px-4 sm:flex-none">
                                    <CreateChallengeIcon size="large" />
                                    Create Challenge
                                </a>
                                <a non-functional href="/challenges/test-idea"
                                    class="btn justify-center items-center !px-4 is-green sm:flex-none">
                                    <span class="btn__icon is-left icon-lightbulb-o1" aria-hidden="true"></span>
                                    Test Idea
                                </a>
                                <a non-functional href="#"
                                    class="btn justify-center items-center is-orange disabled hidden !px-4 sm:flex-none"
                                    @click.prevent>
                                    <span class="btn__icon is-left icon-bulb-plus" aria-hidden="true"></span>
                                    Save Idea
                                </a>
                            </div>
                            <button aria-label="button" non-functional v-else type="button" key="take-a-tour"
                                class="flex items-center text-dust-800 px-4 py-2 bg-dust-50 rounded-md border leading-3 hover:bg-dust-100"
                                @click="() => ($bus('tour:next', { guide: 'start-guide' }))">
                                <NIcon as="onboarding-flow" class="text-2xl mr-2" />
                                <span class="font-bold">Take A Tour</span>
                            </button>
                        </Transition>
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>
